import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ShieldAlert, AlertTriangle, Calendar, Clock, Edit2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { usePharmacies } from '../context/PharmacyContext';
import ExpensePharmacyFilter from '../components/ExpensePharmacyFilter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : BACKEND_URL.endsWith('/') ? `${BACKEND_URL}api` : `${BACKEND_URL}/api`;

function TresorerieEcheances() {
  const { getPharmacyIdsParam } = usePharmacies();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [monthsCount, setMonthsCount] = useState(3);
  const [isCustomMonths, setIsCustomMonths] = useState(false);
  const [customMonthsValue, setCustomMonthsValue] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(null);
  
  const [currentUser] = useState(() => {
    try {
      const stored = sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      return null;
    }
  });

  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState('all');
  
  // Reserve Edit Modal
  const [isEditReserveOpen, setIsEditReserveOpen] = useState(false);
  const [editingReserveMonth, setEditingReserveMonth] = useState(null);
  const [reserveAmount, setReserveAmount] = useState('');
  const [savingReserve, setSavingReserve] = useState(false);

  useEffect(() => {
    fetchData();
  }, [monthsCount, superAdminPharmacyFilter]);

  const fetchData = async () => {
    if (!currentUser?.id) {
      toast.error('Session invalide');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const headers = { 'X-User-Id': currentUser.id };
      
      let pharmacyIds;
      if (currentUser?.role === 'super_admin') {
        pharmacyIds = superAdminPharmacyFilter === 'all' ? 'all' : superAdminPharmacyFilter;
      } else {
        pharmacyIds = getPharmacyIdsParam();
      }

      const response = await axios.get(`${API}/echeances/dashboard`, {
        headers,
        params: {
          pharmacy_ids: pharmacyIds,
          months_count: monthsCount
        }
      });

      setData(response.data);
      if (response.data.months && response.data.months.length > 0 && !selectedMonth) {
        setSelectedMonth(response.data.months[0]);
      } else if (response.data.months && selectedMonth) {
        // Update selected month data if exists
        const updatedSelected = response.data.months.find(m => m.month === selectedMonth.month && m.year === selectedMonth.year);
        if (updatedSelected) {
          setSelectedMonth(updatedSelected);
        }
      }
    } catch (error) {
      console.error('Error fetching echeances data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-DZ', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatCurrencyShort = (amount) => {
    if (amount >= 1000000) {
      return (amount / 1000000).toFixed(1) + 'M';
    }
    if (amount >= 1000) {
      return (amount / 1000).toFixed(1) + 'k';
    }
    return amount.toString();
  };

  const handleOpenReserveEdit = (monthData) => {
    setEditingReserveMonth(monthData);
    setReserveAmount(monthData.deja_reserve.toString());
    setIsEditReserveOpen(true);
  };

  const handleSaveReserve = async () => {
    if (!editingReserveMonth) return;
    
    setSavingReserve(true);
    try {
      const headers = { 'X-User-Id': currentUser.id };
      let pharmacyId = getPharmacyIdsParam();
      if (currentUser?.role === 'super_admin') {
        if (superAdminPharmacyFilter !== 'all') {
          pharmacyId = superAdminPharmacyFilter.split(',')[0]; 
        } else {
          pharmacyId = 'all';
        }
      } else {
        if (pharmacyId === 'all') {
          if (currentUser?.pharmacy_id) {
            pharmacyId = currentUser.pharmacy_id;
          } else if (currentUser?.pharmacies && currentUser.pharmacies.length === 1) {
            pharmacyId = currentUser.pharmacies[0].pharmacy_id || currentUser.pharmacies[0].id;
          }
        } else if (pharmacyId.includes(',')) {
          pharmacyId = 'all'; // Multiple selected, can't save reserve for all
        }
      }

      if (!pharmacyId || pharmacyId === 'all') {
         toast.error("Veuillez sélectionner une pharmacie spécifique pour modifier la réserve.");
         setSavingReserve(false);
         return;
      }
      
      const payload = {
        pharmacy_id: pharmacyId,
        month: editingReserveMonth.month,
        year: editingReserveMonth.year,
        amount: parseFloat(reserveAmount || 0)
      };
      
      await axios.post(`${API}/echeances/reserves`, payload, { headers });
      toast.success('Réserve mise à jour');
      setIsEditReserveOpen(false);
      fetchData(); // Refresh to recalculate Reste
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingReserve(false);
    }
  };

  // Prepare Donut Chart Data (Distribution of suppliers by delay)
  const donutData = useMemo(() => {
    if (!data?.months) return [];
    
    let delays = {
      'Comptant (0 jour)': 0,
      '1 - 15 jours': 0,
      '16 - 30 jours': 0,
      '31 - 60 jours': 0,
      '61 - 90 jours': 0
    };

    let totalOwed = 0;

    data.months.forEach(m => {
      if (m.invoices) {
        m.invoices.forEach(inv => {
          let amount = inv.amount - (inv.paid_amount || 0);
          if (amount <= 0) return;
          totalOwed += amount;
          
          let dateFacture = parseSafeDate(inv.date);
          let due = inv.due_date ? new Date(inv.due_date) : new Date();
          let diffTime = Math.abs(due - dateFacture);
          let diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
          
          if (diffDays <= 0) delays['Comptant (0 jour)'] += amount;
          else if (diffDays <= 15) delays['1 - 15 jours'] += amount;
          else if (diffDays <= 30) delays['16 - 30 jours'] += amount;
          else if (diffDays <= 60) delays['31 - 60 jours'] += amount;
          else delays['61 - 90 jours'] += amount;
        });
      }
    });

    const colors = {
      'Comptant (0 jour)': '#a78bfa',
      '1 - 15 jours': '#60a5fa',
      '16 - 30 jours': '#34d399',
      '31 - 60 jours': '#fbbf24',
      '61 - 90 jours': '#f87171'
    };

    return Object.keys(delays).filter(k => delays[k] > 0).map(k => ({
      name: k,
      value: delays[k],
      color: colors[k]
    }));
  }, [data]);

  const totalDonut = donutData.reduce((acc, curr) => acc + curr.value, 0);

  const kpis = data || {};
  const monthsData = data?.months || [];

  // Compute the date range display
  const dateRangeDisplay = useMemo(() => {
    if (monthsData.length === 0) return '';
    const first = monthsData[0];
    const last = monthsData[monthsData.length - 1];
    return `${first.month_name.split(' ')[0]} ${first.year} - ${last.month_name.split(' ')[0]} ${last.year}`;
  }, [monthsData]);

  if (loading && !data) {
    return <div className="flex items-center justify-center h-screen">
      <div className="text-lg">Chargement...</div>
    </div>;
  }



  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6 bg-[#f0f4f8] min-h-screen">
      
      {/* Header and Controls */}
      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Trésorerie & Échéances</h1>
          <p className="text-slate-500 mt-1 text-sm sm:text-base">
            Visualisez vos paiements fournisseurs et vos remboursements de la sécurité sociale, et identifiez le montant à réserver chaque mois.
          </p>
        </div>
        
        <div className="flex flex-col items-end gap-3">
          <div className="flex items-center gap-3">
            <div className="flex bg-white rounded-lg p-1 border border-slate-200 items-center">
              <button 
                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${monthsCount === 1 && !isCustomMonths ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
                onClick={() => { setMonthsCount(1); setIsCustomMonths(false); }}
              >
                1 mois
              </button>
              <button 
                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${monthsCount === 2 && !isCustomMonths ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
                onClick={() => { setMonthsCount(2); setIsCustomMonths(false); }}
              >
                2 mois
              </button>
              <button 
                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${monthsCount === 3 && !isCustomMonths ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
                onClick={() => { setMonthsCount(3); setIsCustomMonths(false); }}
              >
                3 mois
              </button>
              <button 
                className={`px-3 sm:px-4 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${isCustomMonths ? 'bg-blue-600 text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
                onClick={() => setIsCustomMonths(true)}
              >
                Personnalisé
              </button>
              {isCustomMonths && (
                <div className="ml-2 flex items-center">
                  <Input
                    type="number"
                    min="1"
                    max="48"
                    className="w-16 h-7 sm:h-8 text-xs sm:text-sm px-2 py-1"
                    placeholder="Ex: 6"
                    value={customMonthsValue}
                    onChange={(e) => {
                      const val = e.target.value;
                      setCustomMonthsValue(val);
                      if (val && !isNaN(val) && parseInt(val) > 0) {
                        setMonthsCount(parseInt(val));
                      }
                    }}
                  />
                </div>
              )}
            </div>
            
            {dateRangeDisplay && (
              <div className="hidden md:flex items-center gap-2 bg-white rounded-lg px-4 py-2 border border-slate-200 text-sm font-medium text-slate-700">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="capitalize">{dateRangeDisplay}</span>
              </div>
            )}
          </div>
        </div>
          
          {currentUser?.role === 'super_admin' && (
            <div className="bg-white rounded-lg p-1 border border-slate-200 min-w-[250px]">
              <ExpensePharmacyFilter
                selectedPharmacies={superAdminPharmacyFilter === 'all' ? [] : superAdminPharmacyFilter.split(',')}
                onChange={(ids) => setSuperAdminPharmacyFilter(ids.length === 0 ? 'all' : ids.join(','))}
                currentUser={currentUser}
              />
            </div>
          )}
        </div>

      {/* Top Security KPI */}
      <div className="flex justify-end">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex items-center gap-6 min-w-[350px]">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center shrink-0">
              <ShieldAlert className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">À sécuriser sur les 3 prochains mois</p>
              <p className="text-2xl font-bold text-blue-700">{formatCurrency(kpis.kpi_secure)} DA</p>
            </div>
          </div>
          <div className="hidden sm:block flex-1 border-l border-slate-100 pl-4 space-y-1">
            {monthsData.slice(0, 3).map((m, idx) => (
              <div key={idx} className="flex justify-between items-center text-sm gap-4">
                <span className="text-slate-500 capitalize">{m.month_name.split(' ')[0]}</span>
                <span className="font-semibold text-slate-800">{formatCurrency(m.reste_a_reserver)} DA</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monthly Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {monthsData.slice(0, 4).map((m, idx) => {
          const isCovered = m.reste_a_reserver <= 0;
          return (
            <Card 
              key={m.month_name} 
              className={`border-t-4 cursor-pointer transition-all ${
                selectedMonth?.month_name === m.month_name ? 'ring-2 ring-blue-500 shadow-md' : 'hover:shadow-md'
              } ${
                isCovered ? 'border-t-emerald-500' : (m.reste_a_reserver > 500000 ? 'border-t-rose-500' : 'border-t-amber-500')
              }`}
              onClick={() => setSelectedMonth(m)}
            >
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-3 h-3 rounded-full ${isCovered ? 'bg-emerald-500' : (m.reste_a_reserver > 500000 ? 'bg-rose-500' : 'bg-amber-500')}`}></div>
                  <CardTitle className="text-lg font-bold capitalize">{m.month_name}</CardTitle>
                </div>
                {isCovered ? (
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">Couvert ✓</Badge>
                ) : (
                  <Badge variant="outline" className={m.reste_a_reserver > 500000 ? "text-rose-600 border-rose-200 bg-rose-50" : "text-amber-600 border-amber-200 bg-amber-50"}>
                    {m.reste_a_reserver > 500000 ? "À sécuriser" : "À compléter"}
                  </Badge>
                )}
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="flex justify-between text-slate-600">
                  <span>Fournisseurs à payer</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(m.fournisseurs_a_payer)} DA</span>
                </div>
                <div className="flex justify-between text-slate-600">
                  <span>Sécurité sociale attendue</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(m.secu_attendue)} DA</span>
                </div>
                
                <div className="pt-2 border-t border-slate-100 flex justify-between font-semibold">
                  <span className={m.besoin_a_reserver > 0 ? (m.reste_a_reserver > 500000 ? "text-rose-600" : "text-amber-600") : "text-emerald-600"}>
                    Besoin à réserver
                  </span>
                  <span className={m.besoin_a_reserver > 0 ? (m.reste_a_reserver > 500000 ? "text-rose-600" : "text-amber-600") : "text-emerald-600"}>
                    {formatCurrency(m.besoin_a_reserver)} DA
                  </span>
                </div>
                
                <div className="flex justify-between items-center text-slate-600 group">
                  <span className="flex items-center gap-1">
                    Déjà réservé 
                    <button 
                      onClick={(e) => { e.stopPropagation(); handleOpenReserveEdit(m); }}
                      className="opacity-0 group-hover:opacity-100 transition-opacity p-1 text-slate-400 hover:text-blue-600"
                    >
                      <Edit2 className="w-3 h-3" />
                    </button>
                  </span>
                  <span className="font-medium text-emerald-600">{formatCurrency(m.deja_reserve)} DA</span>
                </div>
                
                <div className="pt-2 border-t border-slate-100 flex justify-between font-bold text-base">
                  <span className="text-slate-800">Reste à réserver</span>
                  <span className={m.reste_a_reserver > 0 ? "text-rose-600" : "text-slate-800"}>
                    {formatCurrency(m.reste_a_reserver)} DA
                  </span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Bar Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center gap-6">
              <CardTitle className="text-lg">Échéances et remboursements par mois</CardTitle>
              <div className="flex items-center gap-4 text-sm font-medium">
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#3b82f6]"></div>Fournisseurs à payer</div>
                <div className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-[#93c5fd]"></div>Sécurité sociale attendue</div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="h-[300px] w-full">
              <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height="100%">
                <BarChart data={monthsData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                  <XAxis dataKey="month_name" axisLine={false} tickLine={false} tickFormatter={(val) => val.substring(0, 3)} />
                  <YAxis axisLine={false} tickLine={false} tickFormatter={(val) => formatCurrencyShort(val)} />
                  <Tooltip 
                    formatter={(value) => [formatCurrency(value) + ' DA', '']}
                    contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                  />
                  <Bar dataKey="fournisseurs_a_payer" name="Fournisseurs à payer" fill="#3b82f6" radius={[4, 4, 0, 0]} barSize={32} />
                  <Bar dataKey="secu_attendue" name="Sécurité sociale" fill="#93c5fd" radius={[4, 4, 0, 0]} barSize={32} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Donut Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg text-center">Répartition des fournisseurs par délai de paiement</CardTitle>
          </CardHeader>
          <CardContent className="flex flex-col items-center">
            <div className="relative h-[200px] w-full">
              <ResponsiveContainer minWidth={0} minHeight={0} width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {donutData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value) + ' DA'} />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <span className="text-xl font-bold text-slate-800">{formatCurrencyShort(totalDonut)}</span>
                <span className="text-xs text-slate-500">Total à payer</span>
              </div>
            </div>
            
            <div className="w-full mt-4 space-y-2">
              {donutData.map((item, i) => (
                <div key={i} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }}></div>
                    <span className="text-slate-600">{item.name}</span>
                  </div>
                  <span className="font-semibold">{formatCurrencyShort(item.value)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tables Row for Selected Month */}
      {selectedMonth && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6 mt-6">
          {/* Table Fournisseurs */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">Échéances fournisseurs - {selectedMonth.month_name}</CardTitle>
              </div>
              <Button variant="link" className="text-blue-600">Voir tout →</Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead>Date échéance</TableHead>
                      <TableHead>Fournisseur</TableHead>
                      <TableHead>Catégorie</TableHead>
                      <TableHead>Référence</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedMonth.invoices.slice(0, 5).map((inv, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium text-slate-700">
                          {new Date(inv.due_date || inv.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </TableCell>
                        <TableCell>{inv.supplier_name}</TableCell>
                        <TableCell className="text-slate-500 text-sm">Achat</TableCell>
                        <TableCell className="text-slate-500 text-sm">{inv.invoice_number || '-'}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(inv.amount - (inv.paid_amount || 0))} DA
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-amber-600 border-amber-200 bg-amber-50 font-normal">À payer</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {selectedMonth.invoices.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-slate-500 py-6">Aucune échéance pour ce mois</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-100 font-bold">
                <span>Total {selectedMonth.month_name.split(' ')[0]}</span>
                <span className="text-blue-600">{formatCurrency(selectedMonth.fournisseurs_a_payer)} DA</span>
              </div>
            </CardContent>
          </Card>

          {/* Table Sécurité Sociale */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-2">
                <CardTitle className="text-base">Remboursements sécurité sociale - {selectedMonth.month_name}</CardTitle>
              </div>
              <Button variant="link" className="text-blue-600">Voir tout →</Button>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-slate-50/50">
                      <TableHead>Organisme</TableHead>
                      <TableHead>Date dépôt</TableHead>
                      <TableHead>Date estimée</TableHead>
                      <TableHead className="text-right">Montant</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedMonth.chifas.slice(0, 5).map((chifa, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-semibold text-slate-800">{chifa.category || 'CNAS'}</TableCell>
                        <TableCell className="text-slate-500">
                          {parseSafeDate(chifa.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </TableCell>
                        <TableCell className="text-slate-500 font-medium">
                          {new Date(chifa.estimated_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' })}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(chifa.montant_bordereau || chifa.amount)} DA
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-blue-600 border-blue-200 bg-blue-50 font-normal">Attendu</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {selectedMonth.chifas.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-slate-500 py-6">Aucun remboursement attendu</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
              <div className="flex justify-between items-center mt-4 pt-4 border-t border-slate-100 font-bold">
                <span>Total {selectedMonth.month_name.split(' ')[0]}</span>
                <span className="text-blue-600">{formatCurrency(selectedMonth.secu_attendue)} DA</span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Reserve Modal */}
      <Dialog open={isEditReserveOpen} onOpenChange={setIsEditReserveOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize">Modifier la réserve - {editingReserveMonth?.month_name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Montant déjà réservé (DA)</Label>
              <Input 
                type="number" 
                value={reserveAmount} 
                onChange={e => setReserveAmount(e.target.value)}
                placeholder="Ex: 500000"
              />
              <p className="text-xs text-slate-500">
                Ce montant sera soustrait du "Besoin à réserver" pour calculer le "Reste à réserver".
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditReserveOpen(false)}>Annuler</Button>
            <Button onClick={handleSaveReserve} disabled={savingReserve}>
              {savingReserve ? 'Sauvegarde...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default TresorerieEcheances;
